"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/motion";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { canvasPalette, canvasRgba } from "@/components/sections/home/stats/data";
import { getGlobeDots } from "@/lib/globe-dots";

/* Interactive particle Earth shared by the home Global Network section and the
   product-page Coverage sections — the same dot-sphere used by the Market Research
   card (shared geometry in `@/lib/globe-dots`), so all globes read as one object:
   land dots sampled against a rasterised country mask, a sparse faint ocean layer
   for the silhouette, and both hemispheres drawn so the far side shows through as
   a translucent sphere.

   What this globe adds on top of the card's version is the interaction layer:
   the 21 CITY_DATA markers, drag-to-spin with momentum, snap-to-country when one
   is selected via `selectedCityIdx`, and hover / pinned tooltips. */

type Rgb = [number, number, number];

const AUTO_SPIN = 0.004; // radians / frame when idle

/** Sphere radius as a fraction of the container — the limb lands at 80% of the
 *  half-size, comfortably inside the rim mask's clear zone below, so the edge
 *  where a dot sphere reads solidest is never erased. */
const GLOBE_R_RATIO = 0.4;

/** Dissolves the canvas at the rim. This MASKS the canvas to transparent rather
 *  than laying page-coloured gradients over it — an overlay would paint a solid
 *  block of `--background` across the section, hiding anything behind the globe
 *  (a section backdrop, a pattern) and leaving a visible pale square. Opaque out
 *  to 88% of the half-size, so it starts well beyond the limb, and `closest-side`
 *  lets it clear the square's corners too. */
const RIM_MASK = "radial-gradient(circle closest-side, #000 88%, transparent 100%)";
/** Land samples per px² of sphere surface, matched to the Market Research card
 *  (9000 samples at a 208px radius) so both globes have the same dot density. */
const DOT_DENSITY = 0.0166;
/** Ocean samples as a fraction of land samples — the card's 1400 / 9000. */
const OCEAN_RATIO = 1400 / 9000;

export const CITY_DATA = [
  {
    lat: 0.647,
    lng: -1.671,
    name: "United States",
    flag: "🇺🇸",
    ips: "3.85M+",
    type: "Mobile & Residential",
  },
  {
    lat: 0.899,
    lng: -0.002,
    name: "United Kingdom",
    flag: "🇬🇧",
    ips: "980K+",
    type: "Mobile & Residential",
  },
  {
    lat: 0.893,
    lng: 0.182,
    name: "Germany",
    flag: "🇩🇪",
    ips: "1.21M+",
    type: "Mobile & Residential",
  },
  { lat: 0.807, lng: 0.039, name: "France", flag: "🇫🇷", ips: "840K+", type: "Residential" },
  { lat: -0.249, lng: -0.907, name: "Brazil", flag: "🇧🇷", ips: "1.42M+", type: "Residential" },
  { lat: 0.632, lng: 2.413, name: "Japan", flag: "🇯🇵", ips: "1.12M+", type: "Residential" },
  {
    lat: 0.359,
    lng: 1.378,
    name: "India",
    flag: "🇮🇳",
    ips: "1.67M+",
    type: "Mobile & Residential",
  },
  {
    lat: 0.98,
    lng: -1.856,
    name: "Canada",
    flag: "🇨🇦",
    ips: "730K+",
    type: "Mobile & Residential",
  },
  { lat: -0.441, lng: 2.335, name: "Australia", flag: "🇦🇺", ips: "510K+", type: "Residential" },
  { lat: 0.626, lng: 1.819, name: "China", flag: "🇨🇳", ips: "2.24M+", type: "Residential" },
  { lat: 1.074, lng: 1.838, name: "Russia", flag: "🇷🇺", ips: "890K+", type: "Residential" },
  { lat: 0.731, lng: 0.219, name: "Italy", flag: "🇮🇹", ips: "620K+", type: "Residential" },
  { lat: 0.706, lng: -0.065, name: "Spain", flag: "🇪🇸", ips: "580K+", type: "Residential" },
  { lat: 0.91, lng: 0.092, name: "Netherlands", flag: "🇳🇱", ips: "430K+", type: "Residential" },
  { lat: 0.627, lng: 2.23, name: "South Korea", flag: "🇰🇷", ips: "710K+", type: "Residential" },
  { lat: 0.412, lng: -1.79, name: "Mexico", flag: "🇲🇽", ips: "640K+", type: "Residential" },
  { lat: -0.014, lng: 1.988, name: "Indonesia", flag: "🇮🇩", ips: "530K+", type: "Residential" },
  { lat: 0.68, lng: 0.615, name: "Turkey", flag: "🇹🇷", ips: "480K+", type: "Residential" },
  { lat: 0.417, lng: 0.787, name: "Saudi Arabia", flag: "🇸🇦", ips: "390K+", type: "Residential" },
  { lat: -0.671, lng: -1.11, name: "Argentina", flag: "🇦🇷", ips: "410K+", type: "Residential" },
  { lat: -0.533, lng: 0.4, name: "South Africa", flag: "🇿🇦", ips: "260K+", type: "Residential" },
];

/** CITY_DATA is stored as lat/lng in radians; the dot sphere works in unit vectors.
 *  Converting once here keeps both using identical rotation math, so a marker always
 *  lands exactly on its continent. */
const CITY_UNITS = CITY_DATA.map((c) => {
  const cosLat = Math.cos(c.lat);
  return {
    ux: cosLat * Math.cos(c.lng),
    uy: Math.sin(c.lat),
    uz: cosLat * Math.sin(c.lng),
  };
});

interface PulseRing {
  x: number;
  y: number;
  r: number;
  maxR: number;
  alpha: number;
  col: number;
}

interface TipState {
  visible: boolean;
  x: number;
  y: number;
  cityIdx: number;
}

/** Screen position of each marker for the current frame — hit-testing and tooltip
 *  placement read this instead of re-deriving the projection. */
interface CityPos {
  x: number;
  y: number;
  z: number;
}

interface Props {
  selectedCityIdx?: number;
  ariaLabel?: string;
}

export default function GlobeComponent({
  selectedCityIdx = -1,
  ariaLabel = "Interactive 3D particle globe showing proxy network coverage",
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const selectedTipRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(0);
  const [tip, setTip] = useState<TipState>({ visible: false, x: 0, y: 0, cityIdx: -1 });

  const S = useRef({
    pulseRings: [] as PulseRing[],
    globeRot: 0,
    tick: 0,
    targetRot: null as number | null,
    isSnapping: false,
  });

  const cityPos = useRef<CityPos[]>(CITY_DATA.map(() => ({ x: 0, y: 0, z: -1 })));
  const isDragging = useRef(false);
  const lastPtrX = useRef(0);
  const momentumRef = useRef(0); // carries spin after pointer release
  const selectedCityIdxRef = useRef(-1);

  const prefersReducedMotion = useReducedMotion();
  const reducedMotionRef = useRef(false);
  const loopRunningRef = useRef(false);
  const startLoopRef = useRef<() => void>(() => {});

  const prefersLight = usePrefersLight();
  const pal = useMemo(() => canvasPalette(prefersLight), [prefersLight]);
  const rgba = useMemo(() => canvasRgba(prefersLight), [prefersLight]);

  /* Full sphere (the card crops to the northern dome; this one shows all of it), and
     the sample count scales with the sphere's surface area. Reusing the card's fixed
     9000 here spread the same dots over ~2.5x the area and the continents dissolved
     into noise — density is what makes the two globes read as the same object, not
     the raw count. Quantised so a resize reuses one of a few cached geometries
     instead of re-rasterising on every frame of a drag. */
  const samples = useMemo(() => {
    const r = Math.max(Math.round((size * GLOBE_R_RATIO) / 40) * 40, 80);
    const land = Math.round(4 * Math.PI * r * r * DOT_DENSITY);
    return { landSamples: land, oceanSamples: Math.round(land * OCEAN_RATIO), minLatDeg: -90 };
  }, [size]);

  const geom = useMemo(
    () => (typeof document === "undefined" ? null : getGlobeDots(samples)),
    [samples]
  );

  useEffect(() => {
    reducedMotionRef.current = !!prefersReducedMotion;
  }, [prefersReducedMotion]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize(Math.floor(e.contentRect.width)));
    ro.observe(el);
    if (el.offsetWidth > 0) setSize(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    selectedCityIdxRef.current = selectedCityIdx;
    if (selectedCityIdx < 0) {
      S.current.isSnapping = false;
      S.current.targetRot = null;
      return;
    }
    // Rotate the shortest way round so the selected country faces the viewer.
    const city = CITY_DATA[selectedCityIdx];
    const rawTarget = Math.PI / 2 - city.lng;
    const cur = S.current.globeRot;
    const diff = ((((rawTarget - cur) % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    S.current.targetRot = cur + diff;
    S.current.isSnapping = true;
    momentumRef.current = 0;
    startLoopRef.current();
  }, [selectedCityIdx]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size === 0 || !geom) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    cancelAnimationFrame(rafRef.current);

    const cx = size / 2;
    const cy = size / 2;
    const rG = size * GLOBE_R_RATIO;
    /* Base land/ocean dots: a mid slate gray in light (the near-black foreground reads
       too heavy), soft white in dark. Sits between the card's #9ea5b2 — which dissolved
       into a ghost spread across a sphere this size on pure white — and the darker
       #6c7486 that made the whole globe read heavy; the alpha boost keeps the
       silhouette solid without the ink-dark weight. */
    const base: Rgb = prefersLight ? [134, 142, 158] : pal.white;
    const dotBoost = prefersLight ? 1.3 : 1;

    const drawLayer = (layer: typeof geom.land, cr: number, sr: number) => {
      // Whole sphere is drawn (no back-face culling) so the far-side dots show through
      // for depth. Far hemisphere first (faint + small), then the near one on top.
      const paint = (nearPass: boolean) => {
        for (const d of layer) {
          const rz = d.ux * sr + d.uz * cr;
          if (nearPass ? rz < 0 : rz >= 0) continue;
          const sy = cy - rG * d.uy;
          const sx = cx + rG * (d.ux * cr - d.uz * sr);
          const front = (rz + 1) / 2; // 0 far .. 1 near
          const d3 = Math.pow(front, 1.5);
          const depth = 0.28 + d3 * 0.72;
          ctx.fillStyle = rgba(base, Math.min(1, d.alpha * depth * dotBoost));
          ctx.beginPath();
          ctx.arc(sx, sy, d.size * (0.62 + d3 * 1.08), 0, Math.PI * 2);
          ctx.fill();
        }
      };
      paint(false);
      paint(true);
    };

    const draw = () => {
      const s = S.current;
      const cr = Math.cos(s.globeRot);
      const sr = Math.sin(s.globeRot);
      const selIdx = selectedCityIdxRef.current;

      ctx.clearRect(0, 0, size, size);
      drawLayer(geom.ocean, cr, sr);
      drawLayer(geom.land, cr, sr);

      // Expanding rings, emitted when a snap lands on its country.
      const aliveRings: PulseRing[] = [];
      for (const ring of s.pulseRings) {
        ring.r += 0.72;
        ring.alpha *= 0.951;
        if (ring.alpha > 0.018 && ring.r < ring.maxR) {
          aliveRings.push(ring);
          ctx.beginPath();
          ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
          ctx.strokeStyle = rgba(ring.col === 1 ? pal.grn : pal.yel, ring.alpha * 0.45);
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      s.pulseRings = aliveRings;

      // Country markers, front face only — alternating yellow / green like before.
      for (let i = 0; i < CITY_UNITS.length; i++) {
        const u = CITY_UNITS[i];
        const rz = u.ux * sr + u.uz * cr;
        const sx = cx + rG * (u.ux * cr - u.uz * sr);
        const sy = cy - rG * u.uy;
        cityPos.current[i] = { x: sx, y: sy, z: rz };
        if (rz < 0.12) continue;

        const col = i % 2 === 0 ? pal.yel : pal.grn;
        const isSelected = i === selIdx;
        const pulse = reducedMotionRef.current
          ? 0.4
          : 0.5 + 0.5 * Math.sin(s.tick * 0.04 + i * 1.7);

        ctx.fillStyle = rgba(col, 0.9);
        ctx.beginPath();
        ctx.arc(sx, sy, isSelected ? 4.2 : 3.0, 0, Math.PI * 2);
        ctx.fill();

        // crisp pulsing ring — no blurry glow, matching the card's beacons
        ctx.strokeStyle = rgba(col, isSelected ? 0.85 : 0.4 * (1 - pulse) + 0.06);
        ctx.lineWidth = isSelected ? 1.5 : 1;
        ctx.beginPath();
        ctx.arc(sx, sy, isSelected ? 8 + pulse * 4 : 4 + pulse * 4, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Pinned tooltip follows the selected marker, written straight to the DOM node.
      const tipEl = selectedTipRef.current;
      if (selIdx >= 0 && tipEl) {
        const cp = cityPos.current[selIdx];
        if (cp.z > 0.05 && !isDragging.current) {
          const tx = Math.min(cp.x + 18, size - 215);
          const ty = Math.max(cp.y - 96, 8);
          tipEl.style.transform = `translate(${tx}px, ${ty}px)`;
          tipEl.style.opacity = String(Math.min((cp.z - 0.05) / 0.2, 1));
        } else {
          tipEl.style.opacity = "0";
        }
      }
    };

    const loop = () => {
      loopRunningRef.current = true;
      const rm = reducedMotionRef.current;
      const s = S.current;
      s.tick++;

      if (s.isSnapping && s.targetRot !== null) {
        const snapDiff = s.targetRot - s.globeRot;
        if (Math.abs(snapDiff) < 0.003) {
          s.globeRot = s.targetRot;
          s.isSnapping = false;
          s.targetRot = null;
          const selIdx = selectedCityIdxRef.current;
          const cp = selIdx >= 0 ? cityPos.current[selIdx] : null;
          if (cp && cp.z > 0) {
            const col = selIdx % 2;
            s.pulseRings.push({ x: cp.x, y: cp.y, r: 0, maxR: 52, alpha: 1.0, col });
            s.pulseRings.push({ x: cp.x, y: cp.y, r: 0, maxR: 34, alpha: 0.85, col });
          }
        } else {
          s.globeRot += snapDiff * 0.07;
          momentumRef.current = 0;
        }
      } else if (!isDragging.current && !rm) {
        momentumRef.current *= 0.92;
        s.globeRot += AUTO_SPIN + momentumRef.current; // base auto-spin + leftover
      }

      draw();

      // Reduced motion: hold a static frame unless the visitor is driving it.
      if (rm && !isDragging.current && !s.isSnapping && s.pulseRings.length === 0) {
        loopRunningRef.current = false;
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    startLoopRef.current = () => {
      if (!loopRunningRef.current) loop();
    };
    loop();
    return () => {
      cancelAnimationFrame(rafRef.current);
      loopRunningRef.current = false;
    };
  }, [size, geom, pal, rgba, prefersLight]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    isDragging.current = true;
    lastPtrX.current = e.clientX;
    momentumRef.current = 0;
    S.current.isSnapping = false;
    S.current.targetRot = null;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    startLoopRef.current();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const delta = e.clientX - lastPtrX.current;
    const sens = 0.007;
    S.current.globeRot -= delta * sens;
    momentumRef.current = -delta * sens * 0.75;
    lastPtrX.current = e.clientX;
    setTip((p) => ({ ...p, visible: false }));
  }, []);

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
    startLoopRef.current();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      let closestIdx = -1;
      let minDist = 40;
      for (let i = 0; i < cityPos.current.length; i++) {
        const cp = cityPos.current[i];
        if (cp.z < 0) continue;
        const dist = Math.hypot(cp.x - mx, cp.y - my);
        if (dist < minDist) {
          minDist = dist;
          closestIdx = i;
        }
      }

      if (closestIdx >= 0) {
        const cp = cityPos.current[closestIdx];
        setTip({
          visible: true,
          x: Math.min(cp.x + 20, size - 205),
          y: Math.max(cp.y - 88, 8),
          cityIdx: closestIdx,
        });
      } else {
        setTip((prev) => ({ ...prev, visible: false }));
      }
    },
    [size]
  );

  const city = tip.visible && tip.cityIdx >= 0 ? CITY_DATA[tip.cityIdx] : null;

  return (
    <div
      ref={containerRef}
      className="relative aspect-square w-full min-w-0 cursor-grab touch-pan-y select-none active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTip((p) => ({ ...p, visible: false }))}
      aria-label={ariaLabel}
    >
      {/* The rim dissolve lives on the canvas as a mask (see RIM_MASK), so the
          section behind the globe stays visible instead of being covered by a
          block of page colour. */}
      <canvas
        ref={canvasRef}
        style={{
          width: size,
          height: size,
          display: "block",
          maskImage: RIM_MASK,
          WebkitMaskImage: RIM_MASK,
        }}
        width={size}
        height={size}
        aria-hidden="true"
      />

      <div
        className="text-foreground pointer-events-none absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5 text-xs font-medium opacity-100 transition-opacity select-none"
        aria-hidden="true"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M5 2 L5 7 M5 2 L3 4 M5 2 L7 4"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 7 C5 7 5 10 7.5 10.5 C9 10.8 9.5 9.5 9.5 9.5 L10 7"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
        <span>Drag to rotate</span>
      </div>

      {/* Hover tooltip — only shown when no city is pinned */}
      {city && selectedCityIdx < 0 && (
        <div
          aria-live="polite"
          className="border-border bg-background/95 pointer-events-none absolute z-40 min-w-[176px] rounded-xl border px-3.5 py-3 shadow-2xl backdrop-blur-md"
          style={{ left: tip.x, top: tip.y }}
        >
          <p className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <span aria-hidden="true">{city.flag}</span>
            {city.name}
          </p>
          <div className="text-muted-foreground space-y-0.5 text-xs">
            <p>
              <span className="text-muted-foreground font-semibold">{city.ips}</span> IPs available
            </p>
            <p className="text-brand-yellow light:text-blue-600">{city.type}</p>
          </div>
        </div>
      )}

      {/* Pinned tooltip — always follows the selected city particle */}
      {selectedCityIdx >= 0 &&
        CITY_DATA[selectedCityIdx] &&
        (() => {
          const sc = CITY_DATA[selectedCityIdx];
          const cc =
            selectedCityIdx % 2 === 0
              ? "text-brand-yellow light:text-blue-600"
              : "text-green-500 light:text-green-700";
          return (
            <div
              ref={selectedTipRef}
              aria-live="polite"
              className="bg-background/95 pointer-events-none absolute z-50 min-w-[176px] rounded-xl border px-3.5 py-3 shadow-2xl backdrop-blur-md"
              style={{
                top: 0,
                left: 0,
                transform: "translate(0px, 0px)",
                opacity: 0,
                transition: "opacity 0.25s ease",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
            >
              <p className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
                <span aria-hidden="true">{sc.flag}</span>
                {sc.name}
              </p>
              <div className="text-muted-foreground space-y-0.5 text-xs">
                <p>
                  <span className="text-foreground/75 font-semibold">{sc.ips}</span> IPs available
                </p>
                <p className={cc}>{sc.type}</p>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
